import { useCallback, useMemo } from 'react'
import {
	createLoader,
	createParser,
	createSerializer,
	inferParserType,
	parseAsArrayOf,
	parseAsString,
	parseAsStringLiteral,
	useQueryStates,
	type ParserMap,
	type UseQueryStatesKeysMap,
} from 'nuqs'
import type { ListMemoryPort } from '@thom/folio-domain/core/ports/list-memory'
import { ADDRESSABLE_KINDS } from '@thom/folio-domain/core/domain/entry'
import { ISSUE_STATUSES, PRIORITIES } from '@thom/folio-domain/core/domain/issue'
import { PLAN_STATUSES } from '@thom/folio-domain/core/domain/plan'
import {
	ENTRY_SORT_FIELDS,
	LOG_SORT_FIELDS,
	PLAN_SORT_FIELDS,
	TICKET_SORT_FIELDS,
	SORT_DIRECTION,
	SORT_DIRECTIONS,
	type Sort,
} from '@thom/folio-domain/core/ports/sort'

const LIST = {
	TICKETS: '/_authenticated/$client/$domain/$slug/tickets/',
	PLANS: '/_authenticated/$client/$domain/$slug/plans/',
	JOURNAL: '/_authenticated/$client/$domain/$slug/journal/',
	LOGS: '/_authenticated/$client/$domain/$slug/logs/',
	KNOWLEDGE: '/_authenticated/knowledge/',
} as const

const member = <T extends string>(allowed: readonly T[], value: string | undefined): T | undefined =>
	allowed.find(candidate => candidate === value)

const parseAsSort = <T extends string>(fields: readonly T[]) =>
	createParser<Sort<T>>({
		parse: value => {
			const [rawField, rawDirection] = value.split(',')
			const field = member(fields, rawField)

			return field === undefined
				? null
				: { field, direction: member(SORT_DIRECTIONS, rawDirection) ?? SORT_DIRECTION.DESC }
		},
		serialize: sort => `${sort.field},${sort.direction}`,
	})

const parseAsFlag = createParser<true>({
	parse: value => (value === 'true' ? true : null),
	serialize: () => 'true',
})

const strings = parseAsArrayOf(parseAsString)

const ticketsFilters = {
	statuses: parseAsArrayOf(parseAsStringLiteral(ISSUE_STATUSES)),
	priority: parseAsStringLiteral(PRIORITIES),
	tags: strings,
	q: parseAsString,
	archived: parseAsFlag,
	sort: parseAsSort(TICKET_SORT_FIELDS),
}

const plansFilters = {
	ticket: parseAsString,
	statuses: parseAsArrayOf(parseAsStringLiteral(PLAN_STATUSES)),
	tags: strings,
	q: parseAsString,
	sort: parseAsSort(PLAN_SORT_FIELDS),
}

const journalFilters = {
	kinds: parseAsArrayOf(parseAsStringLiteral(ADDRESSABLE_KINDS)),
	ticket: parseAsString,
	tags: strings,
	q: parseAsString,
	sort: parseAsSort(ENTRY_SORT_FIELDS),
}

const logsFilters = {
	ticket: parseAsString,
	plan: parseAsString,
	q: parseAsString,
	sort: parseAsSort(LOG_SORT_FIELDS),
}

const knowledgeFilters = {
	q: parseAsString,
}

type Filters<P extends UseQueryStatesKeysMap> = {
	readonly [K in keyof P]?: NonNullable<inferParserType<P[K]>>
}

type Patch<P extends UseQueryStatesKeysMap> = {
	readonly [K in keyof P]?: NonNullable<inferParserType<P[K]>> extends readonly (infer U)[]
		? readonly U[]
		: NonNullable<inferParserType<P[K]>>
}

const isEmpty = (value: unknown): boolean =>
	value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

const present = <P extends UseQueryStatesKeysMap>(values: Record<string, unknown>): Filters<P> =>
	Object.fromEntries(Object.entries(values).filter(([, value]) => !isEmpty(value))) as Filters<P>

const useListFilters = <P extends UseQueryStatesKeysMap>(parsers: P) => {
	const [values, setValues] = useQueryStates(parsers, { history: 'push' })

	const filters = useMemo(() => present<P>(values), [values])

	const setFilter = useCallback(
		(patch: Patch<P>) => {
			const cleared = Object.fromEntries(
				Object.entries(patch).map(([key, value]) => [key, isEmpty(value) ? null : value]),
			)
			void setValues(cleared as never)
		},
		[setValues],
	)

	return [filters, setFilter] as const
}

const PARSERS: Record<string, ParserMap> = {
	[LIST.TICKETS]: ticketsFilters,
	[LIST.PLANS]: plansFilters,
	[LIST.JOURNAL]: journalFilters,
	[LIST.LOGS]: logsFilters,
}

const isRememberedList = (list: string): boolean => list in PARSERS

type RawSearch = Record<string, unknown>

const asQueryValue = (value: unknown): string =>
	Array.isArray(value) ? value.map(String).join(',') : typeof value === 'object' ? JSON.stringify(value) : String(value)

const typed = (list: string, raw: RawSearch): Record<string, unknown> => {
	const parsers = PARSERS[list]
	if (!parsers) return {}

	const input = Object.fromEntries(
		Object.entries(raw)
			.filter(([, value]) => value !== undefined && value !== null)
			.map(([key, value]) => [key, asQueryValue(value)]),
	)

	return present(createLoader(parsers)(input))
}

const rememberFilters = (memory: ListMemoryPort, list: string, raw: RawSearch): void => {
	memory.remember(list, typed(list, raw))
}

const restoreFilters = (memory: ListMemoryPort, list: string, raw: RawSearch): Record<string, string> | undefined => {
	const parsers = PARSERS[list]
	const restored = parsers && memory.restore(list, typed(list, raw))
	if (!parsers || !restored) return undefined

	const query = createSerializer(parsers)(restored as never)

	return Object.fromEntries(new URLSearchParams(query))
}

export {
	isRememberedList,
	journalFilters,
	knowledgeFilters,
	LIST,
	logsFilters,
	plansFilters,
	rememberFilters,
	restoreFilters,
	ticketsFilters,
	useListFilters,
}
